import { Ghost, colorType } from 'components/Buttons';

import React from 'react';
import { routes } from 'routes';
import { payFrequency } from 'types';
import {
  projectTypes,
  issuanceTypes,
  stocksTypeType,
  colorByProjectType,
  issuanceType,
} from 'types/projectType';
import { formatNumber } from 'util/formatNumber';
import { calculatePercentage } from 'util/percent';
import { idProjectEnergiaSolar } from 'types/idProject';
import { useMediaQuery } from 'hooks';
import { Bold, Text } from 'components/Texts';
import { Div, weight } from 'globalStyle';
import styled from 'styled-components';
import { List, ListItem } from '@material-ui/core';
import { getContrastTextColor } from 'util/contrast';
import { calculateAmountStocks } from 'util/calculateAmountStocks';

const detailsComponent = (project, isDesktop) => {
  const paymentFrequency = project.projectId === idProjectEnergiaSolar
    ? 'Cuatrimestral'
    : payFrequency[project?.frequencyInterest];
  const baseDetails = [
    {
      name: 'Retorno estimado:',
      value: `${calculatePercentage(project?.ieaTax)}% E.A.`,
      differentRows: true,
    },
    { name: 'Tiempo de financiación:', value: project?.investmentTimeDescription, differentRows: true },
  ];

  const equityDesktopDetails = [...baseDetails];

  const equityMobileDetails = [
    { name: 'Recolectado:', value: formatNumber(project.currentAmount, null, project?.currencyIso) },
    ...baseDetails,
  ];

  const tokensDetails = [
    {
      name: 'Valor mínimo de inversión:',
      value: formatNumber(calculateAmountStocks(project), null, project?.currencyIso),
      differentRows: true,
    },
    {
      name: 'Tipo de venta',
      value: issuanceType[project.issuanceType],
      differentRows: true,
    },
  ];

  const stocksDetails = [
    {
      name:
        project.issuanceType === issuanceTypes.INDIVIDUAL
          ? 'Valor mínimo de inversión:'
          : 'Valor de la acción:',
      value: formatNumber(project.nodeValue, null, project?.currencyIso),
      differentRows: true,
    },
    {
      name:
        project.issuanceType === issuanceTypes.INDIVIDUAL
          ? 'Tipo de acción'
          : 'Valor mínimo de inversión:',
      value:
        project.issuanceType === issuanceTypes.INDIVIDUAL
          ? stocksTypeType[project.stockType] || 'Definir'
          : formatNumber(calculateAmountStocks(project), null, project?.currencyIso),
      differentRows: true,
    },
  ];

  const defaultDesktopDetails = [
    { name: 'Tasa:', value: `${calculatePercentage(project?.ieaTax)}% E.A.` },
    {
      name: 'Garantía:',
      value: (
        <>
          {project?.warranty
            ? ` ${calculatePercentage(project?.warranty)}%`
            : 'Sin garantía'}
          {project?.isWarranty && (
            <>
              <Ghost
                ml={8}
                underline
                size={13}
                color="inherit"
                onColorType={colorType.BLUE}
                href={`${routes.warranty.route}/${project?.warrantyCompanyId}`}
              >
                <span>Conoce la garantía</span>
              </Ghost>
              <br />
            </>
          )}
        </>
      ),
    },
    { name: 'Período de pago:', value: paymentFrequency },
    {
      name: 'Tiempo de financiación:',
      value: `${project?.returnTime} ${
        project?.returnTime === 1 ? 'mes' : 'meses'
      }`,
    },
  ];

  const defaultDetails = [
    { name: 'Recolectado:', value: `${formatNumber(project.currentAmount, null, project?.currencyIso)}` },
    ...defaultDesktopDetails,
  ];

  if (project.projectType === projectTypes.EQUITY) {
    return isDesktop ? equityDesktopDetails : equityMobileDetails;
  }

  if (project.projectType === projectTypes.TOKENS) {
    return tokensDetails;
  }

  if (project.projectType === projectTypes.STOCKS) {
    return stocksDetails;
  }

  return isDesktop ? defaultDesktopDetails : defaultDetails;
};

function DetailComponent({
  name, value, differentRows, isDesktop, color,
}) {
  if (isDesktop && differentRows) {
    return (
      <Div
        isFlex
        direction="column"
        padding="10px 0"
        width="50%"
        style={{ rowGap: '5px' }}
      >
        <Text color={color} align="center">
          {name}
        </Text>
        <span
          style={{
            fontWeight: weight.BOLD,
            marginLeft: '3px',
            color: `${color}`,
          }}
        >
          {value}
        </span>
      </Div>
    );
  }
  if (isDesktop) {
    return (
      <Text
        color={color}
        width="50%"
        align="center"
        style={{ padding: '10px 0' }}
      >
        {name}
        <Bold color={color} style={{ marginLeft: 8 }}>
          {value}
        </Bold>
      </Text>
    );
  }
  return (
    <ListItem
      style={{
        display: 'list-item',
        color,
        padding: '0px',
        marginLeft: '20px',
        marginBottom: '4px',
      }}
    >
      <Div>
        <Text color={color} style={{ display: 'inline' }}>
          {name}
        </Text>
        <Bold color={color} style={{ marginLeft: 8 }}>
          {value}
        </Bold>
      </Div>
    </ListItem>
  );
}

export default function DetailsComponent({ project, color: bgColor }) {
  const isDesktop = useMediaQuery(1023);
  const details = detailsComponent(project, isDesktop);
  const color = getContrastTextColor(
    bgColor || colorByProjectType[project?.projectType],
  );

  if (!isDesktop) {
    return (
      <List
        disablePadding
        style={{
          listStyle: 'disc',
          paddingTop: '16px',
        }}
      >
        {details.map((detail) => (
          <DetailComponent {...detail} color={color} isDesktop={isDesktop} />
        ))}
      </List>
    );
  }
  return (
    <Container color={color}>
      {details.map((detail) => (
        <DetailComponent {...detail} color={color} isDesktop={isDesktop} />
      ))}
    </Container>
  );
}

const Container = styled.div`
  width: 100%;
  display: flex; 
  justify-content: space-around;
  align-items: center;
  position: relative;
  flex-wrap: wrap;

  :before,
  :after {
    content: '';
    position: absolute;
    background: ${({ color }) => color};
    }

    :before {
      top: 0;
      left: 50%;
      right: 0;
      width: 1px;
      height: 100%;
      transform: translateX(-50%);
    }
    :after {
      top: 50%;
      bottom: 0;
      left: 0;
      height: 1px;
      width: 100%;
      transform: translateY(-50%);
    }
    :not(:has(:nth-child(3)))::after {
      display: none;
    }
  }
`;
